import {
    ClientDVIR,
    ClientFleetCompliance,
    ClientPMSchedule,
    ClientWorkOrder,
    ComplianceAlert,
    ComplianceNotificationSettings,
    ComplianceScopeUpdate,
    CostRange,
    DeductionLedgerEntityType,
    FleetChecklistItemConfig,
    FleetStatus,
    FleetUnitType,
    GroupByDate,
    InspectionResolutionStatus,
    LoadSearchFullPartial,
    SkybitzAuthMode,
    Truck,
    TruckOperationalStatus,
    TruckOwnershipType,
    WorkOrderStatus,
    type BoxTruckAssetProfile,
    type Equipment,
    type FleetMapLoad,
    type TruckFuelConfig,
} from '@numeo/types-core'
import { FleetDriverType, type WorkOrderChargeState } from '@numeo/types-main-web-app'
import axios, { type AxiosRequestConfig } from 'axios'
import { getServerErrorMessage, getUserErrorMessage } from 'utils/get-user-error-message'
import type { GetAssigneesResponse, GetTrucksResponse } from './driverPayrollApi'
import { inspectionMultipartBody } from './inspectionAttachmentsApi'
export type { FleetMapLoad } from '@numeo/types-core'
// Re-export the shared cost-dashboard contract so existing feature imports (services/fleetApi) keep working.
export { CostRange, GroupByDate, TruckOperationalStatus }

const API_BASE_URL = '/fleet'
export type FleetMutationRequestOptions = Pick<AxiosRequestConfig, 'signal' | 'timeout'>

// ─── Overview ───

export interface FleetOverviewData {
    activeTrucks: number
    totalTrucks: number
    activeTrailers: number
    totalTrailers: number
    outOfService: number
    inShop: number
    inactiveTrucks: number
    unassignedTrailers: number
    complianceAlerts: { expiring: number; expired: number }
    openWorkOrders: number
    overduePMs: number
    inspectionsToday: number
    openDefects: number
}

export const getFleetOverview = async (): Promise<FleetOverviewData> => {
    const { data } = await axios.get(`${API_BASE_URL}/overview`)
    return data
}

export enum UnitStatusCategory {
    ActiveTrucks = 'activeTrucks',
    InShop = 'inShop',
    OutOfService = 'outOfService',
    InactiveTrucks = 'inactiveTrucks',
    ActiveTrailers = 'activeTrailers',
    UnassignedTrailers = 'unassignedTrailers',
}

// ─── Maintenance Cost Dashboard ───

export interface MaintenanceKpis {
    total_cost: number
    cost_change_pct: number
    cost_change_value: number
    /**
     * Null when the endpoint cannot source in-period miles — render '—', not a number.
     * `truck.mileage` is a lifetime odometer, so dividing one month's spend by it produced a
     * figure two orders of magnitude below any real per-mile cost.
     */
    cost_per_mile: number | null
    cost_per_mile_change: number | null
    preventive_ratio: number
    downtime_hours: number
    downtime_change: number
    avg_cost_per_event: number
    avg_cost_change: number
    active_trucks: number
    serviced_trucks: number
    events: number
    open_order_cost: number
    open_order_count: number
    open_invoices_count: number
}

export interface CostTrendItem {
    month: string
    value: number
    year?: number
}

export interface MaintenanceExpenseReportDetail {
    id: string
    date: string
    unitId: string
    unitName: string
    unitType?: 'Truck' | 'Trailer'
    reason: string
    serviceType: string
    vendor: string
    paymentMethod: string
    paymentAccount: string
    cover: string
    amount: number
    description: string
}

export interface MaintenanceExpenseReport {
    periodStart: string
    periodEnd: string
    totalExpenses: number
    unitCount: number
    details: MaintenanceExpenseReportDetail[]
}

export const getMaintenanceExpenseReport = async (params: { from: string; to: string }): Promise<MaintenanceExpenseReport> => {
    const { data } = await axios.get(`${API_BASE_URL}/maintenance-expense-report`, { params })
    return data.data
}

// Optional custom [from, to] window (ISO date strings) — used when range = Custom.
export interface CostQueryOptions {
    groupBy?: GroupByDate
    from?: string
    to?: string
}

export interface TruckCostItem {
    truck_id: string
    // Canonical unit id (the truck/trailer's own _id) — pass this, not truck_id, when drilling into
    // getUnitInvoices, since truck_id is a display name and may not be unique across a fleet.
    unit_id: string
    // Unit type resolved server-side — a work order's unit can be a truck or a trailer.
    type?: 'Truck' | 'Trailer'
    // Shown beside the number: a unit number changes and gets reused, the VIN does not.
    vin?: string
    // The unit is no longer in the fleet, so `truck_id` is the number recorded on its work orders at
    // the time rather than a live lookup.
    unit_archived?: boolean
    cost: number
    percent: number
    change_pct: number
    events: number
}

export type MaintenanceDriverType = FleetDriverType

export interface DriverTypeCostItem {
    driver_type: MaintenanceDriverType
    cost: number
    percent: number
    events: number
}

export interface UnitInvoiceItem {
    invoice_number: string | null
    vendor: string | null
    invoice_date: string | null
    service_type: string | null
    description: string | null
    amount: number
    // The uploaded invoice document, when the order came from a scan. Null for hand-entered orders.
    invoice_file_url: string | null
}

export interface UnitInvoicesResponse {
    unit_name: string
    unit_vin?: string
    unit_type?: 'Truck' | 'Trailer'
    // See TruckCostItem.unit_archived — the name is the one recorded at the time, not a live lookup.
    unit_archived?: boolean
    period_start: string
    period_end: string
    total_cost: number
    items: UnitInvoiceItem[]
}

const withCostParams = (base: Record<string, string | number>, opts?: CostQueryOptions): Record<string, string | number> => {
    const params = { ...base }
    if (opts?.groupBy) params.groupBy = opts.groupBy
    if (opts?.from) params.from = opts.from
    if (opts?.to) params.to = opts.to
    return params
}

export const getMaintenanceKpis = async (opts?: CostQueryOptions): Promise<MaintenanceKpis> => {
    const { data } = await axios.get(`${API_BASE_URL}/overview/kpis`, { params: withCostParams({}, opts) })
    return data.data
}

export const getCostTrend = async (range: CostRange = CostRange.OneMonth, opts?: CostQueryOptions): Promise<CostTrendItem[]> => {
    const { data } = await axios.get(`${API_BASE_URL}/overview/cost-trend`, { params: withCostParams({ range }, opts) })
    return data.data
}

export const getTruckCosts = async (
    month?: number,
    allTime?: boolean,
    range: CostRange = CostRange.OneMonth,
    opts?: CostQueryOptions
): Promise<TruckCostItem[]> => {
    const base: Record<string, string | number> = { range }
    if (month != null) base.month = month
    if (allTime) base.allTime = 1
    const { data } = await axios.get(`${API_BASE_URL}/overview/truck-costs`, { params: withCostParams(base, opts) })
    return data.data
}

export const getDriverTypeCosts = async (range: CostRange = CostRange.OneMonth, opts?: CostQueryOptions): Promise<DriverTypeCostItem[]> => {
    const base: Record<string, string | number> = { range }
    const fallbackMessage = 'Unable to load driver-type maintenance costs. Please try again.'

    try {
        const { data } = await axios.get<{ data?: unknown }>(`${API_BASE_URL}/overview/driver-type-costs`, {
            params: withCostParams(base, opts),
        })
        // Do not hand malformed data to the table: a proxy/login page or an incomplete response
        // would otherwise fail later in render with an unhelpful `map is not a function` message.
        if (!Array.isArray(data.data)) throw new Error(fallbackMessage)
        return data.data as DriverTypeCostItem[]
    } catch (error) {
        // Let cancellation retain Axios semantics; callers may use it to abandon an obsolete range.
        if (axios.isCancel(error)) throw error
        if (axios.isAxiosError(error)) throw new Error(getServerErrorMessage(error) ?? fallbackMessage)
        throw error instanceof Error ? error : new Error(fallbackMessage)
    }
}

// `unassigned` opens a row for work orders saved with no unit, which have no id to ask for. Those rows
// are kept apart by the number their invoice recorded, so `number` says which one — absent means the
// row for orders whose invoice recorded no number at all. Deliberately its own flag rather than a magic
// unitId value: the endpoint keeps rejecting an empty unitId, so a dropped parameter cannot quietly
// return the leftovers instead.
export const getUnitInvoices = async (
    unitId: string,
    month?: number,
    range: CostRange = CostRange.OneMonth,
    opts?: CostQueryOptions,
    unassigned = false,
    number?: string
): Promise<UnitInvoicesResponse> => {
    const base: Record<string, string | number> = { range }
    if (unassigned) {
        base.unassigned = 1
        if (number) base.number = number
    } else base.unitId = unitId
    if (month != null) base.month = month
    const { data } = await axios.get(`${API_BASE_URL}/overview/unit-invoices`, { params: withCostParams(base, opts) })
    return data.data
}

// ─── Equipment ───

export interface FleetEquipmentTruck {
    id: string
    _id?: string
    truckId?: string
    unitNumber?: string
    mainInfo?: string
    make?: string
    model?: string
    year?: number
    truckYear?: number | string
    licensePlate?: string
    state?: string
    vin?: string
    equipment?: Equipment
    length?: number | string
    weight?: number | string
    fullPartial?: LoadSearchFullPartial
    ppDeviceId?: string
    boxTruck?: BoxTruckAssetProfile
    status?: string
    assignedDriver?: string
    drivers?: string[]
    driverName?: string
    driverNames?: string[]
    boxTruckOwnerName?: string
    ownershipType?: string
    mileage?: number
    faults?: { critical: number; warning: number }
    additionalNotes?: string
    fuelConfig?: TruckFuelConfig
    // Permits & Restrictions: the same shapes the truck itself carries.
    excludedStates?: string[]
    restrictions?: string[]
    isPermitted?: Truck['isPermitted']
    security?: Truck['security']
    eldIntegration?: { type?: string; vehicleId?: string; integrationId?: string }
    terminatedAt?: string | Date
}

export type FleetTruckFuelConfigUpdate = Partial<Record<'tankCapacityGallons' | 'mpg' | 'minReservePercent', number | null>>
export type FleetTruckUpdate = Partial<Omit<FleetEquipmentTruck, 'fuelConfig' | 'ownershipType' | 'year'>> & {
    year?: number | null
    fuelConfig?: FleetTruckFuelConfigUpdate
    /** `null` clears the stored type. */
    ownershipType?: TruckOwnershipType | null
    /** `null` clears the truck's Fleet status. */
    fleetStatus?: FleetStatus | null
}

export interface FleetEquipmentTrailer {
    id: string
    /** `null` clears the trailer's Fleet status. */
    fleetStatus?: FleetStatus | null
    unitNumber?: string
    make?: string
    model?: string
    year?: number
    trailerType?: string
    licensePlate?: string
    state?: string
    serialNumber?: string
    status?: string
    assignedTruckId?: string
    // GPS & Location
    gps?: { lat: number; lng: number; speed?: number; heading?: number; timestamp: string; state: string; source?: string }
    gpsState?: string
    gpsAge?: string
    location?: string
    // Fleet management
    alerts?: { type: string; message: string; severity: string; createdAt: string }[]
    utilization?: number
    nextInspection?: string
    lastInspection?: string
    odometer?: number
    totalCost?: number
    ownershipType?: string
    vin?: string
    // Assignment & booking
    driver?: string
    booked?: boolean
    bookingLoad?: string
    notes?: string
    requests?: number
    // Phillips Connect
    phillipsConnectAssetId?: string
    phillipsConnectData?: { cargo?: { floorSpacePercent?: number; volumePercent?: number; imageUrl?: string; loaded?: boolean } }
    gpsSource?: string
}

export interface FleetEquipmentResponse {
    trucks: FleetEquipmentTruck[]
    trailers: FleetEquipmentTrailer[]
}

export const getFleetEquipment = async (options: { forceRefresh?: boolean } = {}): Promise<FleetEquipmentResponse> => {
    const params = options.forceRefresh ? { forceRefresh: 'true' } : undefined
    const { data } = await axios.get(`${API_BASE_URL}/equipment`, { params })
    return data
}

// ─── Trailer Locations (live map) ───

/**
 * Poll-friendly trailer payload for the live Fleet View map. Backed by
 * `GET /fleet/equipment/trailer-locations`, which is a projected read of the tenant's trailers —
 * unlike `getFleetEquipment`, it does no fault-code sync and no Application/ongoing-loads reads,
 * so it is safe to call on an interval.
 */
export interface FleetTrailerLocation {
    id: string
    unitNumber?: string
    trailerType?: string
    location?: string
    gps?: { lat: number; lng: number; speed?: number; heading?: number; timestamp: string; state: string; source?: string }
    gpsState?: string
    gpsAge?: string
    gpsSource?: string
}

export const getTrailerLocations = async (): Promise<{ trailers: FleetTrailerLocation[] }> => {
    const { data } = await axios.get(`${API_BASE_URL}/equipment/trailer-locations`)
    return data
}

// ─── Trailer Stats ───

export interface TrailerStatsResponse {
    total: number
    available: number
    inTransit: number
    repair: number
    idle: number
    reserved: number
    needsAttention: number
}

export const getTrailerStats = async (): Promise<TrailerStatsResponse> => {
    const { data } = await axios.get(`${API_BASE_URL}/equipment/trailer-stats`)
    return data
}

// ─── Fleet Trailers ───

export const createFleetTrailer = async (trailerData: Partial<FleetEquipmentTrailer>): Promise<FleetEquipmentTrailer> => {
    const { data } = await axios.post(`${API_BASE_URL}/trailers`, trailerData)
    return data
}

export const bulkImportTrailers = async (
    trailers: Record<string, unknown>[],
    // Set by the ELD-backed import so the server pulls live locations from the integration the rows
    // actually came from, rather than guessing when a project has more than one of that vendor.
    eldIntegrationId?: string
): Promise<{
    created: number
    skipped: number
    deviceConflicts: number
    failed: number
    skippedUnitNumbers: string[]
    deviceConflictUnitNumbers: string[]
}> => {
    const { data } = await axios.post(`${API_BASE_URL}/trailers/bulk`, { trailers, eldIntegrationId })
    return data.data
}

// ─── Phillips Connect Integration ───

export interface PhillipsConnectIntegration {
    id: string
    type: 'phillips_connect'
    applicationId: string
    enabled: boolean
    webhookSecret: string
    companyName?: string
    lastEventAt?: string
    createdAt: string
    updatedAt: string
}

export const getPhillipsConnectSetup = async (): Promise<PhillipsConnectIntegration | null> => {
    const { data } = await axios.get(`${API_BASE_URL}/integrations/phillips-connect`)
    return data.data
}

export const enablePhillipsConnect = async (companyName?: string): Promise<PhillipsConnectIntegration> => {
    const { data } = await axios.post(`${API_BASE_URL}/integrations/phillips-connect/enable`, { companyName })
    return data.data
}

export const disablePhillipsConnect = async (): Promise<void> => {
    await axios.post(`${API_BASE_URL}/integrations/phillips-connect/disable`)
}

export const regeneratePhillipsConnectSecret = async (): Promise<PhillipsConnectIntegration> => {
    const { data } = await axios.post(`${API_BASE_URL}/integrations/phillips-connect/regenerate-secret`)
    return data.data
}

// ─── Skybitz Integration ───

export interface SkybitzIntegration {
    id: string
    type: 'skybitz'
    applicationId: string
    enabled: boolean
    webhookSecret: string
    companyName?: string
    hasCredentials?: boolean
    authMode?: SkybitzAuthMode
    lastEventAt?: string
    createdAt: string
    updatedAt: string
}

/**
 * Skybitz tells the user exactly what is wrong with their credentials, and the endpoint passes
 * that through; without unwrapping, the caller only ever sees axios' own "Request failed with
 * status code 400".
 */
const toSkybitzError = (error: unknown, fallback: string): Error => new Error(getUserErrorMessage(error) || fallback)

export const getSkybitzSetup = async (): Promise<SkybitzIntegration | null> => {
    const { data } = await axios.get(`${API_BASE_URL}/integrations/skybitz`)
    return data.data
}

export const enableSkybitz = async (config: {
    companyName?: string
    clientId: string
    clientSecret: string
    authMode: SkybitzAuthMode
}): Promise<SkybitzIntegration> => {
    try {
        const { data } = await axios.post(`${API_BASE_URL}/integrations/skybitz/enable`, config)
        return data.data
    } catch (error) {
        throw toSkybitzError(error, 'Failed to enable Skybitz')
    }
}

export const disableSkybitz = async (): Promise<void> => {
    await axios.post(`${API_BASE_URL}/integrations/skybitz/disable`)
}

export const regenerateSkybitzSecret = async (): Promise<SkybitzIntegration> => {
    const { data } = await axios.post(`${API_BASE_URL}/integrations/skybitz/regenerate-secret`)
    return data.data
}

export const triggerSkybitzPull = async (): Promise<{ processed: number; errors: number }> => {
    try {
        const { data } = await axios.post(`${API_BASE_URL}/integrations/skybitz/pull`)
        return data.data
    } catch (error) {
        throw toSkybitzError(error, 'Failed to sync Skybitz data')
    }
}

export const updateFleetTrailer = async (trailerId: string, trailerData: Partial<FleetEquipmentTrailer>): Promise<FleetEquipmentTrailer> => {
    const { data } = await axios.put(`${API_BASE_URL}/trailers/${trailerId}`, trailerData)
    return data
}

export const deleteFleetTrailer = async (trailerId: string): Promise<void> => {
    await axios.delete(`${API_BASE_URL}/trailers/${trailerId}`)
}

/**
 * Delete one or more trucks from `application.temp.spotFinder.trucks`.
 * Uses the existing shared endpoint at `DELETE /trucks` which accepts `{ ids: string[] }`
 * in the body — not a route under `/fleet`, so this one bypasses `API_BASE_URL`.
 */
export const deleteFleetTrucks = async (truckIds: string[]): Promise<{ deletedCount: number }> => {
    const { data } = await axios.delete('/trucks', { data: { ids: truckIds } })
    // `deletedCount` is what the server actually removed (0..N); default to the requested count for
    // older backends that don't return it, so callers can trust the number is never over-reported.
    return { deletedCount: typeof data?.deletedCount === 'number' ? data.deletedCount : truckIds.length }
}

// ─── Trailer History ───

export enum TrailerHistoryEventType {
    WORK_ORDER = 'work_order',
    DVIR = 'dvir',
    COMPLIANCE = 'compliance',
}

export interface TrailerHistoryEvent {
    type: TrailerHistoryEventType
    title: string
    description: string
    date: string
    status?: string
}

export const getTrailerHistory = async (trailerId: string): Promise<{ events: TrailerHistoryEvent[] }> => {
    const { data } = await axios.get(`${API_BASE_URL}/trailers/${trailerId}/history`)
    return data.data
}

export const createFleetTruck = async (truckData: Partial<FleetEquipmentTruck>, acceptOverage?: boolean): Promise<FleetEquipmentTruck> => {
    // `acceptOverage` acknowledges the per-truck overage cost (ADR-020) so an active subscriber can
    // add past the included-truck block; the server then bumps the Stripe truck quantity.
    const { data } = await axios.post(`${API_BASE_URL}/trucks/`, { ...truckData, acceptOverage })
    return data
}

export const updateFleetTruck = async (truckId: string, truckData: FleetTruckUpdate): Promise<FleetEquipmentTruck> => {
    const { data } = await axios.put(`${API_BASE_URL}/trucks/${truckId}`, truckData)
    return data
}

// ─── Map Loads ───

export const getFleetMapLoads = async (): Promise<FleetMapLoad[]> => {
    const { data } = await axios.get(`${API_BASE_URL}/map-loads`)
    return data
}

// ─── Compliance ───

export const getFleetCompliance = async (): Promise<ClientFleetCompliance[]> => {
    const { data } = await axios.get(`${API_BASE_URL}/compliance`)
    return data
}

export const getFleetComplianceAlerts = async (): Promise<ComplianceAlert[]> => {
    const { data } = await axios.get(`${API_BASE_URL}/compliance/alerts`)
    return data?.data ?? []
}

export interface ConnectedEmailAccount {
    email: string
    provider: string
    grantId: string
    isExpired: boolean
}

export const getConnectedEmailAccounts = async (): Promise<ConnectedEmailAccount[]> => {
    const { data } = await axios.get('/email/accounts')
    return data?.accounts ?? []
}

export const getComplianceNotificationSettings = async (): Promise<ComplianceNotificationSettings | null> => {
    const { data } = await axios.get(`${API_BASE_URL}/compliance/notifications/settings`)
    return data?.data ?? null
}

export const updateComplianceNotificationSettings = async (settings: { fleet?: ComplianceScopeUpdate; safety?: ComplianceScopeUpdate }): Promise<void> => {
    await axios.put(`${API_BASE_URL}/compliance/notifications/settings`, settings)
}

export const getComplianceConnectCommand = async (scope: 'fleet' | 'safety'): Promise<string> => {
    const { data } = await axios.get(`${API_BASE_URL}/compliance/notifications/connect-command`, { params: { scope } })
    return data?.data?.command ?? ''
}

export const updateTruckCompliance = async (truckId: string, complianceData: Partial<ClientFleetCompliance>): Promise<ClientFleetCompliance> => {
    const { data } = await axios.put(`${API_BASE_URL}/compliance/truck/${truckId}`, complianceData)
    return data
}

export const updateTrailerCompliance = async (trailerId: string, complianceData: Partial<ClientFleetCompliance>): Promise<ClientFleetCompliance> => {
    const { data } = await axios.put(`${API_BASE_URL}/compliance/trailer/${trailerId}`, complianceData)
    return data
}

export const reassignComplianceUnit = async (recordId: string, unitId: string): Promise<ClientFleetCompliance> => {
    const { data } = await axios.put(`${API_BASE_URL}/compliance/${recordId}/unit`, { unitId })
    return data
}

export const uploadComplianceDocument = async (
    file: File,
    metadata?: { unitId?: string; unitType?: string; docType?: string; issuanceDate?: string | null }
): Promise<{ success: boolean; documentUrl: string }> => {
    try {
        const formData = new FormData()
        formData.append('file', file)
        if (metadata?.unitId) formData.append('unitId', metadata.unitId)
        if (metadata?.unitType) formData.append('unitType', metadata.unitType)
        if (metadata?.docType) formData.append('docType', metadata.docType)
        if (metadata?.issuanceDate) formData.append('issuanceDate', metadata.issuanceDate)
        const { data } = await axios.post(`${API_BASE_URL}/compliance/documents/upload`, formData)
        return data
    } catch (error) {
        console.error('Failed to upload compliance document:', error)
        throw error
    }
}

// ─── Compliance checklist config ───

export interface FleetChecklistItemInput {
    label: string
    unitTypes: FleetUnitType[]
}

export const getFleetChecklistConfig = async (): Promise<FleetChecklistItemConfig[]> => {
    const { data } = await axios.get(`${API_BASE_URL}/compliance/checklist-config`)
    return data?.data ?? data
}

export const addFleetChecklistItem = async (item: FleetChecklistItemInput): Promise<FleetChecklistItemConfig[]> => {
    const { data } = await axios.post(`${API_BASE_URL}/compliance/checklist-config`, item)
    return data?.data ?? data
}

export const updateFleetChecklistItem = async (key: string, updates: Partial<FleetChecklistItemInput>): Promise<FleetChecklistItemConfig[]> => {
    const { data } = await axios.put(`${API_BASE_URL}/compliance/checklist-config/${key}`, updates)
    return data?.data ?? data
}

export const removeFleetChecklistItem = async (key: string): Promise<FleetChecklistItemConfig[]> => {
    const { data } = await axios.delete(`${API_BASE_URL}/compliance/checklist-config/${key}`)
    return data?.data ?? data
}

export const deleteComplianceDocument = async (metadata: { unitId: string; unitType: string; docType: string }): Promise<void> => {
    await axios.delete(`${API_BASE_URL}/compliance/documents`, { data: metadata })
}

// ─── Maintenance Invoice AI Parsing ───

/**
 * @deprecated The legacy immediate pipeline: the file goes to agents-service (`processPaperwork`, Gemini there) and the
 * request waits for it. Use ADR-022's immediate mode instead: `POST /documents/processing/immediate/v1` with
 * `docTypeHint: 'maintenance_invoice'`, then `GET /documents/processing/jobs/:jobId/v1` on a 202 (the contract: ADR-022 Immediate mode).
 * Existing callers move once the `maintenance_invoice` type passes its eval and its output type is in `@numeo/types` (ADR-022.2 decision 9). Do not add new callers.
 */
export const parseMaintenanceInvoice = async (
    file: File,
    mergeSessionId?: string
): Promise<{ success: boolean; data: import('@numeo/types-core').MaintenanceInvoiceParseResult }> => {
    const formData = new FormData()
    formData.append('file', file)
    const { data } = await axios.post(`${API_BASE_URL}/work-orders/parse-invoice`, formData, {
        ...(mergeSessionId && { headers: { 'x-merge-invoice-session-id': mergeSessionId } }),
    })
    return data
}

export const uploadMergedWorkOrderInvoice = async (file: File, mergeSessionId: string): Promise<string> => {
    const formData = new FormData()
    formData.append('file', file)
    const { data } = await axios.post<{ success: boolean; data?: { invoiceFileUrl?: string }; message?: string }>(
        `${API_BASE_URL}/work-orders/upload-merged-invoice`,
        formData,
        { headers: { 'x-merge-invoice-session-id': mergeSessionId } }
    )
    const invoiceFileUrl = data.data?.invoiceFileUrl
    if (!data.success || !invoiceFileUrl) throw new Error(data.message || 'Failed to upload merged invoice')
    return invoiceFileUrl
}

export const deleteMergedInvoiceSession = async (mergeSessionId: string): Promise<void> => {
    await axios.delete(`${API_BASE_URL}/work-orders/merged-invoice-session/${encodeURIComponent(mergeSessionId)}`)
}

// ─── Fault Codes ───

export interface ClientFaultCode {
    id: string
    projectName: string
    vehicleId: string
    truckId: string
    spnId?: number
    fmiId?: number
    code: string
    description: string
    severity: 'warning' | 'critical'
    status: 'active' | 'resolved'
    source?: string
    firstSeenAt: string
    resolvedAt?: string
    resolvedBy?: 'samsara' | 'manual'
}

export const getTruckFaultCodes = async (truckId: string): Promise<{ success: boolean; data: ClientFaultCode[] }> => {
    const { data } = await axios.get(`${API_BASE_URL}/trucks/${truckId}/fault-codes`)
    return data
}

export const resolveFaultCode = async (faultId: string): Promise<{ success: boolean; message?: string }> => {
    const { data } = await axios.patch(`${API_BASE_URL}/fault-codes/${faultId}/resolve`)
    return data
}

// ─── Work Orders ───

export interface WorkOrderFilterParams {
    status?: WorkOrderStatus
    unitId?: string
}

const WORK_ORDERS_PAGE_SIZE = 200
const WORK_ORDERS_MAX_PAGES = 50

export const getWorkOrders = async (filters?: WorkOrderFilterParams): Promise<ClientWorkOrder[]> => {
    const workOrders: ClientWorkOrder[] = []
    for (let page = 0; page < WORK_ORDERS_MAX_PAGES; page++) {
        const queryParams = new URLSearchParams()
        if (filters?.status) queryParams.append('status', filters.status)
        if (filters?.unitId) queryParams.append('unitId', filters.unitId)
        queryParams.append('page', String(page))
        queryParams.append('limit', String(WORK_ORDERS_PAGE_SIZE))
        const { data } = await axios.get(`${API_BASE_URL}/work-orders?${queryParams.toString()}`)
        workOrders.push(...data)
        if (data.length < WORK_ORDERS_PAGE_SIZE) break
    }
    return workOrders
}

export const getWorkOrderServiceTypes = async (): Promise<string[]> => {
    const { data } = await axios.get<string[]>(`${API_BASE_URL}/work-orders/service-types`)
    return data
}

export const addWorkOrderServiceType = async (name: string): Promise<string> => {
    const { data } = await axios.post<string>(`${API_BASE_URL}/work-orders/service-types`, { name })
    return data
}

export const createWorkOrder = async (workOrderData: Partial<ClientWorkOrder>, options?: FleetMutationRequestOptions): Promise<ClientWorkOrder> => {
    const { data } = await axios.post(`${API_BASE_URL}/work-orders`, workOrderData, options)
    return data
}

export const getWorkOrderById = async (id: string): Promise<ClientWorkOrder> => {
    const { data } = await axios.get(`${API_BASE_URL}/work-orders/${id}`)
    return data
}

export const updateWorkOrder = async (id: string, workOrderData: Partial<ClientWorkOrder>): Promise<ClientWorkOrder> => {
    const { data } = await axios.put(`${API_BASE_URL}/work-orders/${id}`, workOrderData)
    return data
}

export const deleteWorkOrder = async (id: string): Promise<void> => {
    await axios.delete(`${API_BASE_URL}/work-orders/${id}`)
}

// ─── Work Order → Driver/Contractor Charge (payroll deduction) ───

export interface WorkOrderChargePayload {
    entityId: string
    entityType: DeductionLedgerEntityType
    entityName?: string
    amount: number
    splitCount?: number
    date?: string
    description?: string
}

export const getWorkOrderCharge = async (id: string): Promise<WorkOrderChargeState | null> => {
    const { data } = await axios.get(`${API_BASE_URL}/work-orders/${id}/charge`)
    return data.data
}

export const createWorkOrderCharge = async (
    id: string,
    payload: WorkOrderChargePayload,
    options?: FleetMutationRequestOptions
): Promise<WorkOrderChargeState> => {
    const { data } = await axios.post(`${API_BASE_URL}/work-orders/${id}/charge`, payload, options)
    return data.data
}

export const updateWorkOrderCharge = async (id: string, payload: Partial<WorkOrderChargePayload>): Promise<WorkOrderChargeState> => {
    const { data } = await axios.put(`${API_BASE_URL}/work-orders/${id}/charge`, payload)
    return data.data
}

export const deleteWorkOrderCharge = async (id: string): Promise<void> => {
    await axios.delete(`${API_BASE_URL}/work-orders/${id}/charge`)
}

// Driver/truck lookups for the maintenance charge form. FLEET-scoped copies of the payroll
// assignees/trucks endpoints, so a user with Fleet access (but no Accounting access) can still
// populate the "charge to driver / contractor" dropdowns. Response shapes match the payroll API.
export const getFleetAssignees = async (): Promise<GetAssigneesResponse> => {
    try {
        const { data } = await axios.get<GetAssigneesResponse>(`${API_BASE_URL}/maintenance/assignees`)
        return data
    } catch (error) {
        console.error('Error getting fleet assignees:', error)
        if (axios.isAxiosError(error)) {
            return { success: false, message: error.response?.data?.message || 'Failed to get drivers' }
        }
        return { success: false, message: 'Failed to get drivers' }
    }
}

export const getFleetTrucks = async (): Promise<GetTrucksResponse> => {
    try {
        const { data } = await axios.get<GetTrucksResponse>(`${API_BASE_URL}/maintenance/trucks`)
        return data
    } catch (error) {
        console.error('Error getting fleet trucks:', error)
        if (axios.isAxiosError(error)) {
            return { success: false, message: error.response?.data?.message || 'Failed to get trucks' }
        }
        return { success: false, message: 'Failed to get trucks' }
    }
}

export const bulkDeleteWorkOrders = async (ids: string[]): Promise<void> => {
    const { data, status } = await axios.delete(`${API_BASE_URL}/work-orders`, { data: { ids } })
    if (status === 207) throw new Error(data.message)
}

export interface BulkActionResult {
    // false when the server only partially applied the change (HTTP 207) — some ids didn't match
    success: boolean
    message?: string
}

export const bulkCompleteWorkOrders = async (ids: string[]): Promise<BulkActionResult> => {
    const { data, status } = await axios.put(`${API_BASE_URL}/work-orders/bulk-complete`, { ids })
    return status === 207 ? { success: false, message: data.message } : { success: true }
}

export const bulkReopenWorkOrders = async (ids: string[]): Promise<BulkActionResult> => {
    const { data, status } = await axios.put(`${API_BASE_URL}/work-orders/bulk-reopen`, { ids })
    return status === 207 ? { success: false, message: data.message } : { success: true }
}

// ─── PM Schedules ───

export const getPMSchedules = async (): Promise<ClientPMSchedule[]> => {
    const { data } = await axios.get(`${API_BASE_URL}/pm-schedules`)
    return data
}

export const createPMSchedule = async (scheduleData: Partial<ClientPMSchedule>): Promise<ClientPMSchedule> => {
    const { data } = await axios.post(`${API_BASE_URL}/pm-schedules`, scheduleData)
    return data
}

export const uploadPMScheduleInvoice = async (file: File): Promise<string> => {
    const formData = new FormData()
    formData.append('file', file)
    const { data } = await axios.post<{ success: boolean; data?: { invoiceFileUrl?: string }; message?: string }>(
        `${API_BASE_URL}/pm-schedules/upload-invoice`,
        formData
    )
    const invoiceFileUrl = data.data?.invoiceFileUrl
    if (!data.success || !invoiceFileUrl) throw new Error(data.message || 'Failed to upload invoice')
    return invoiceFileUrl
}

export const deletePMScheduleInvoice = async (invoiceFileUrl: string): Promise<void> => {
    await axios.delete(`${API_BASE_URL}/pm-schedules/upload-invoice`, { data: { invoiceFileUrl } })
}

export const getPMScheduleById = async (id: string): Promise<ClientPMSchedule> => {
    const { data } = await axios.get(`${API_BASE_URL}/pm-schedules/${id}`)
    return data
}

export const updatePMSchedule = async (id: string, scheduleData: Partial<ClientPMSchedule>): Promise<ClientPMSchedule> => {
    const { data } = await axios.put(`${API_BASE_URL}/pm-schedules/${id}`, scheduleData)
    return data
}

export const deletePMSchedule = async (id: string): Promise<void> => {
    await axios.delete(`${API_BASE_URL}/pm-schedules/${id}`)
}

export const bulkDeletePMSchedules = async (ids: string[]): Promise<void> => {
    const { data, status } = await axios.delete(`${API_BASE_URL}/pm-schedules`, { data: { ids } })
    if (status === 207) throw new Error(data.message)
}

// ─── DVIRs ───

export interface DVIRFilterParams {
    driverId?: string
    unitId?: string
    onlyVoided?: boolean
}

export const getDVIRs = async (filters?: DVIRFilterParams): Promise<ClientDVIR[]> => {
    const queryParams = new URLSearchParams()
    if (filters?.driverId) queryParams.append('driverId', filters.driverId)
    if (filters?.unitId) queryParams.append('unitId', filters.unitId)
    if (filters?.onlyVoided) queryParams.append('onlyVoided', 'true')
    const { data } = await axios.get(`${API_BASE_URL}/dvirs?${queryParams.toString()}`)
    return data
}

export const createDVIR = async (dvirData: Partial<ClientDVIR>, files: File[] = []): Promise<ClientDVIR> => {
    const { data } = await axios.post(`${API_BASE_URL}/dvirs`, inspectionMultipartBody(dvirData, files))
    return data
}

export const getDVIRById = async (id: string): Promise<ClientDVIR> => {
    const { data } = await axios.get(`${API_BASE_URL}/dvirs/${id}`)
    return data
}

export const updateDefectStatus = async (dvirId: string, defectId: string, updateData: { status: string; workOrderId?: string }): Promise<ClientDVIR> => {
    const { data } = await axios.put(`${API_BASE_URL}/dvirs/${dvirId}/defects/${defectId}`, updateData)
    return data
}

export const updateDVIRResolutionStatus = async (id: string, resolutionStatus: InspectionResolutionStatus): Promise<ClientDVIR> => {
    const { data } = await axios.patch(`${API_BASE_URL}/dvirs/${id}/status`, { resolutionStatus })
    return data
}

export const voidDVIR = async (id: string, reason: string): Promise<ClientDVIR> => {
    const { data } = await axios.patch(`${API_BASE_URL}/dvirs/${id}/void`, { reason })
    return data
}

export const restoreDVIR = async (id: string): Promise<ClientDVIR> => {
    const { data } = await axios.patch(`${API_BASE_URL}/dvirs/${id}/restore`)
    return data
}
